import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import LanguageSelector from '../components/LanguageSelector.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocale();

  const [form, setForm] = useState({ workplace: '', phone: '', bio: '', languages: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Golfers don't need onboarding
  if (user?.role !== 'caddie') {
    navigate('/feed');
    return null;
  }

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.caddies.updateProfile(form);
      await refreshUser();
      navigate('/feed');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🎒</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('completeProfile')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('privateContactNote')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('golfCourse')}
            </label>
            <input
              className="input"
              type="text"
              placeholder="e.g. Mahanakhon Golf Club"
              value={form.workplace}
              onChange={set('workplace')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('phoneNumber')}
            </label>
            <input
              className="input"
              type="tel"
              placeholder="+66 xx xxx xxxx"
              value={form.phone}
              onChange={set('phone')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('languagesSpoken')}
            </label>
            <LanguageSelector
              value={form.languages}
              onChange={(languages) => setForm((current) => ({ ...current, languages }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('shortBio')}
            </label>
            <textarea
              className="input min-h-[100px] resize-none"
              placeholder="Tell golfers about your experience, the courses you know, etc."
              value={form.bio}
              onChange={set('bio')}
              maxLength={500}
            />
            <p className="text-xs text-gray-400 mt-1">{form.bio.length}/500</p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/feed')}
              className="btn-secondary flex-1"
            >
              {t('skip')}
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? t('saving') : t('saveProfile')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
